import { Key, Zap } from 'lucide-react';

/** Canonical event key for forgot-password workflows. */
export const FORGOT_PASSWORD_EVENT_KEY = 'forgot_password_requested';

export const FORGOT_PASSWORD_LEGACY_EVENT_KEYS = [
  'forgot_password_requested',
  'auth.forgot_password_requested',
  'user.password_reset_requested',
  'password_reset_requested',
  'receive_forgot_password_request',
  'client.password_reset_requested',
];

export const FORGOT_PASSWORD_TRIGGER = {
  id: FORGOT_PASSWORD_EVENT_KEY,
  eventKey: FORGOT_PASSWORD_EVENT_KEY,
  name: 'Forgot password requested',
  description: 'Starts when a portal user requests a password reset link.',
  icon: Key,
  color: 'emerald',
  category: 'authentication',
  aliases: [
    'receive_forgot_password_request',
    'password_reset_requested',
    'forgot_password',
    'auth.forgot_password_requested',
    'user.password_reset_requested',
    'client.password_reset_requested',
  ],
  keywords: [
    'forgot password',
    'password reset',
    'reset request',
    'receive forgot password request',
    'reset link',
    'auth',
    'authentication',
    'portal user',
    'user password',
    'portal password reset',
    'user requested reset',
  ],
  legacyLabels: [
    'receive forgot password request',
    'forgot password requested',
    'password reset requested',
    'forgot password',
  ],
};

export const WORKFLOW_TRIGGER_CATALOG = [
  {
    id: 'authentication',
    name: 'Authentication',
    triggers: [FORGOT_PASSWORD_TRIGGER],
  },
  {
    id: 'system_triggers',
    name: 'System Triggers',
    triggers: [],
  },
];

const ALL_CATALOG_TRIGGERS = WORKFLOW_TRIGGER_CATALOG.flatMap((group) => group.triggers);

function normalizeSearchText(value) {
  return String(value || '').toLowerCase().trim();
}

function collectTriggerSearchTerms(trigger) {
  return [
    trigger.id,
    trigger.eventKey,
    trigger.name,
    trigger.description,
    trigger.category,
    ...(trigger.aliases || []),
    ...(trigger.keywords || []),
    ...(trigger.legacyLabels || []),
  ]
    .filter(Boolean)
    .map(normalizeSearchText);
}

export function findTriggerCatalogEntry(keyOrRecord) {
  if (!keyOrRecord) return null;

  const rawKey = typeof keyOrRecord === 'string'
    ? keyOrRecord
    : (
      keyOrRecord.key
      || keyOrRecord.eventKey
      || keyOrRecord.trigger_type
      || keyOrRecord.event_id
      || keyOrRecord.id
      || keyOrRecord.config?.event_key
      || keyOrRecord.config?.trigger_key
      || keyOrRecord.config?.event
    );

  const normalizedKey = normalizeSearchText(rawKey).replace(/\s+/g, '_');
  const direct = ALL_CATALOG_TRIGGERS.find((trigger) => (
    normalizeSearchText(trigger.id) === normalizedKey
    || normalizeSearchText(trigger.eventKey) === normalizedKey
    || trigger.aliases?.some((alias) => normalizeSearchText(alias) === normalizedKey)
  ));
  if (direct) return direct;

  if (typeof keyOrRecord !== 'string' && isForgotPasswordTrigger(keyOrRecord)) {
    return FORGOT_PASSWORD_TRIGGER;
  }

  return null;
}

function textIndicatesForgotPassword(value) {
  const text = normalizeSearchText(value).replace(/_/g, ' ');
  if (!text) return false;
  return (
    text.includes('forgot password')
    || text.includes('password reset')
    || text.includes('reset request')
    || text.includes('receive forgot password')
    || text.includes('reset link')
    || text.includes('portal password reset')
  );
}

export function isForgotPasswordTrigger(record) {
  if (!record) return false;

  const eventKey = normalizeSearchText(
    record.key
    || record.eventKey
    || record.trigger_type
    || record.event_id
    || record.id
    || record.config?.event_key
    || record.config?.trigger_key
    || record.config?.event,
  );

  if (FORGOT_PASSWORD_LEGACY_EVENT_KEYS.some((key) => normalizeSearchText(key) === eventKey)) {
    return true;
  }

  const labelText = [
    record.name,
    record.label,
    record.trigger_name,
    record.description,
    record.config?.description,
  ].filter(Boolean).join(' ');

  if (textIndicatesForgotPassword(labelText)) {
    return true;
  }

  const stepId = normalizeSearchText(record.id).replace(/-/g, '_');
  if (stepId.includes('receive_forgot_password') || stepId.includes('forgot_password')) {
    return true;
  }

  // Misnamed agency.created only when step clearly indicates forgot-password behavior.
  if (eventKey === 'agency.created' && textIndicatesForgotPassword(labelText)) {
    return true;
  }

  return false;
}

export function resolveTriggerFromLegacy(record) {
  if (!record) return null;

  if (isForgotPasswordTrigger(record)) {
    return {
      id: FORGOT_PASSWORD_EVENT_KEY,
      eventKey: FORGOT_PASSWORD_EVENT_KEY,
      name: FORGOT_PASSWORD_TRIGGER.name,
      description: FORGOT_PASSWORD_TRIGGER.description,
      trigger_type: FORGOT_PASSWORD_EVENT_KEY,
      event_id: FORGOT_PASSWORD_EVENT_KEY,
      category: FORGOT_PASSWORD_TRIGGER.category,
    };
  }

  const catalogEntry = findTriggerCatalogEntry(record);
  if (!catalogEntry) return null;

  return {
    id: catalogEntry.eventKey,
    eventKey: catalogEntry.eventKey,
    name: catalogEntry.name,
    description: catalogEntry.description,
    trigger_type: catalogEntry.eventKey,
    event_id: catalogEntry.eventKey,
    category: catalogEntry.category,
  };
}

export function triggerMatchesQuery(trigger, query) {
  const q = normalizeSearchText(query);
  if (!q) return true;

  const terms = collectTriggerSearchTerms(trigger);
  if (terms.some((term) => term.includes(q) || q.includes(term))) {
    return true;
  }

  return terms.some((term) => term.replace(/[._]/g, ' ').includes(q.replace(/[._]/g, ' ')));
}

export function filterTriggerCatalogGroups(groups, query) {
  const trimmed = (query || '').trim();
  if (!trimmed) return groups;

  return groups
    .map((group) => ({
      ...group,
      triggers: (group.triggers || []).filter((trigger) => triggerMatchesQuery(trigger, trimmed)),
    }))
    .filter((group) => (group.triggers || []).length > 0);
}

export function getTriggerCatalogGroups() {
  return WORKFLOW_TRIGGER_CATALOG.map((group) => ({
    ...group,
    triggers: [...(group.triggers || [])],
  })).filter((group) => group.triggers.length > 0);
}

export function createTriggerSelectionFromCatalog(catalogTrigger) {
  const entry = findTriggerCatalogEntry(catalogTrigger) || catalogTrigger;
  if (!entry) return null;

  return {
    id: entry.eventKey || entry.id,
    eventKey: entry.eventKey || entry.id,
    name: entry.name,
    label: entry.name,
    description: entry.description,
    trigger_type: entry.eventKey || entry.id,
    event_id: entry.eventKey || entry.id,
    type: entry.eventKey || entry.id,
    icon: entry.icon || Zap,
    color: entry.color || 'emerald',
    category: entry.category,
    config: {
      event_key: entry.eventKey || entry.id,
      trigger_key: entry.eventKey || entry.id,
      event: entry.eventKey || entry.id,
      description: entry.description,
      enabled: true,
      configured: true,
    },
  };
}

export function normalizeTriggerStep(step) {
  if (!step || step.type !== 'trigger') return step;

  const resolved = resolveTriggerFromLegacy(step);
  if (!resolved) return step;

  return {
    ...step,
    label: resolved.name,
    name: resolved.name,
    description: resolved.description || step.description,
    config: {
      ...(step.config || {}),
      trigger_key: resolved.eventKey,
      event_key: resolved.eventKey,
      event: resolved.eventKey,
      description: resolved.description || step.config?.description,
    },
  };
}
